// Custom hook for WebSocket connection management

import { useState, useEffect, useCallback, useRef } from 'react';
import { websocketService } from '../services/websocketService';

export const useWebSocket = (token) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isMainAppConnected, setIsMainAppConnected] = useState(false);
  const [error, setError] = useState(null);
  const [receivedData, setReceivedData] = useState(null);
  const hasConnectedRef = useRef(false);

  const connectWebSocket = useCallback(async () => {
    try {
      await websocketService.connect(token);

      // Setup event listeners
      websocketService.on('connected', () => {
        setIsConnected(true);
        setError(null);
      });

      websocketService.on('authenticated', () => {
        setIsAuthenticated(true);
      });

      websocketService.on('authFailed', ({ error }) => {
        setError(error);
        setIsAuthenticated(false);
      });

      websocketService.on('pairedStatusChanged', ({ isConnected }) => {
        setIsMainAppConnected(isConnected);
      });

      websocketService.on('dataReceived', (data) => {
        setReceivedData(data);
      });

      websocketService.on('disconnected', () => {
        setIsConnected(false);
        setIsAuthenticated(false);
        setIsMainAppConnected(false);
      });

      websocketService.on('error', ({ message }) => {
        setError(message);
      });

    } catch (err) {
      setError(err.message);
    }
  }, [token]);

  useEffect(() => {
    if (token && !hasConnectedRef.current) {
      connectWebSocket();
      hasConnectedRef.current = true;
    }

    return () => {
      if (hasConnectedRef.current) {
        websocketService.disconnect();
        hasConnectedRef.current = false;
      }
    };
  }, [token, connectWebSocket]);

  const sendData = useCallback((data) => {
    return websocketService.sendData(data);
  }, []);

  const sendCommand = useCallback((activityId, commandId) => {
    return websocketService.sendCommand(activityId, commandId);
  }, []);

  const checkPairedStatus = useCallback(() => {
    websocketService.checkPairedStatus();
  }, []);

  const disconnect = useCallback(() => {
    websocketService.disconnect();
    setIsConnected(false);
    setIsAuthenticated(false);
    setIsMainAppConnected(false);
  }, []);

  const attachIframe = useCallback((iframeWindow) => {
    websocketService.attachIframe(iframeWindow);
  }, []);

  const detachIframe = useCallback(() => {
    websocketService.detachIframe();
  }, []);

  return {
    isConnected,
    isAuthenticated,
    isMainAppConnected,
    error,
    receivedData,
    sendData,
    sendCommand,
    checkPairedStatus,
    disconnect,
    attachIframe,
    detachIframe
  };
};
